import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from "react-leaflet";
import {
  FiActivity,
  FiCalendar,
  FiClock,
  FiFilter,
  FiMapPin,
  FiNavigation,
  FiRefreshCcw,
  FiSearch,
  FiTrendingUp,
  FiUser,
  FiUsers,
} from "react-icons/fi";
import { LuRoute, LuSignal } from "react-icons/lu";
import L from "leaflet";
import API from "../services/api";
import "./AdminEmployeeTrackingHistoryPage.css";
import MapApiStatusBadge from "./MapApiStatusBadge";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const toDateInputValue = (date) => {
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 10);
};

const getDefaultFilters = () => {
  const today = new Date();
  const from = new Date(today);
  from.setDate(today.getDate() - 6);
  return {
    employeeId: "",
    from: toDateInputValue(from),
    to: toDateInputValue(today),
  };
};

const FitMapBounds = ({ points = [] }) => {
  const map = useMap();

  useEffect(() => {
    if (!Array.isArray(points) || points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 15);
      return;
    }
    map.fitBounds(points, { padding: [32, 32] });
  }, [map, points]);

  return null;
};

const fmtDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fmtDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const fmtDistance = (valueMeters) => {
  const meters = Number(valueMeters);
  if (!Number.isFinite(meters) || meters <= 0) return "0 m";
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(2)} km`;
};

const fmtMinutesAgo = (mins) => {
  if (mins == null) return "—";
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `${hours}h ${rem}m ago` : `${hours}h ago`;
};

const fmtDuration = (minutes) => {
  const total = Number(minutes);
  if (!Number.isFinite(total) || total <= 0) return "0m";
  if (total < 60) return `${total}m`;
  const hours = Math.floor(total / 60);
  const rem = total % 60;
  return rem ? `${hours}h ${rem}m` : `${hours}h`;
};

const labelForEvent = (type) => {
  if (type === "punch_in") return "Punch In";
  if (type === "punch_out") return "Punch Out";
  if (type === "meeting_start") return "Meeting Start";
  if (type === "meeting_end") return "Meeting End";
  return "Location Update";
};

const toneForEvent = (type) => {
  if (type === "punch_in") return "start";
  if (type === "punch_out") return "end";
  if (type === "meeting_start" || type === "meeting_end") return "meeting";
  return "update";
};

const coordsOf = (entry) => {
  const lat = Number(entry?.location?.lat ?? entry?.latestLocation?.lat);
  const lng = Number(entry?.location?.lng ?? entry?.latestLocation?.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
};

const formatCoords = (entry) => {
  const lat = Number(entry?.location?.lat ?? entry?.latestLocation?.lat);
  const lng = Number(entry?.location?.lng ?? entry?.latestLocation?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "—";
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
};

const getInitials = (name = "") =>
  String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "EM";

const isStopPoint = (point, index, points = []) => {
  if (point?.type !== "location_update") return false;
  const segment = Number(point?.segmentDistanceMeters || 0);
  const nextSegment = Number(points[index + 1]?.segmentDistanceMeters || 0);
  return segment <= 15 || nextSegment <= 15;
};

const getMarkerIcon = (index, point, points = []) => {
  const tone = toneForEvent(point?.type);
  const stopped = isStopPoint(point, index, points);
  const label =
    point?.type === "punch_in"
      ? "IN"
      : point?.type === "punch_out"
        ? "OUT"
        : point?.type === "meeting_start"
          ? "MS"
          : point?.type === "meeting_end"
            ? "ME"
            : stopped
              ? "ST"
              : `${index + 1}`;

  return L.divIcon({
    className: "athp-marker-shell",
    html: `<div class="athp-marker is-${tone} ${stopped ? "is-stop" : ""}"><span>${label}</span></div>`,
    iconSize: [38, 38],
    iconAnchor: [19, 19],
    popupAnchor: [0, -16],
  });
};

const getSessionState = (record) => {
  if (record?.isLive) return { label: "Live", tone: "success" };
  if (record?.punchInAt && record?.punchOutAt) return { label: "Completed", tone: "dark" };
  if (record?.punchInAt) return { label: "Open Session", tone: "warning" };
  return { label: "No Punch", tone: "secondary" };
};

const InfoRow = ({ icon, label, value }) => (
  <div className="athp-info-row">
    <span className="athp-info-icon">{icon}</span>
    <span className="athp-info-label">{label}</span>
    <span className="athp-info-value">{value || "—"}</span>
  </div>
);

const AdminEmployeeTrackingHistoryPage = () => {
  const [filters, setFilters] = useState(() => getDefaultFilters());
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedAttendanceId, setSelectedAttendanceId] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const fetchHistory = async (nextFilters = filters, silent = false) => {
    if (nextFilters.from && nextFilters.to && nextFilters.from > nextFilters.to) {
      setError("From date To date se bada nahi ho sakta.");
      return;
    }

    if (silent) setRefreshing(true);
    else setLoading(true);

    try {
      const res = await API.get("/admin/attendance-tracking-history", {
        params: {
          employeeId: nextFilters.employeeId || undefined,
          from: nextFilters.from || undefined,
          to: nextFilters.to || undefined,
        },
      });

      const nextEmployees = Array.isArray(res.data?.employees) ? res.data.employees : [];
      const nextRecords = Array.isArray(res.data?.records) ? res.data.records : [];

      setEmployees(nextEmployees);
      setRecords(nextRecords);
      setSummary(res.data?.summary || null);
      setError("");
      setSelectedAttendanceId((prev) => {
        if (!prev) return nextRecords[0]?.attendanceId || "";
        return nextRecords.some((item) => item.attendanceId === prev) ? prev : nextRecords[0]?.attendanceId || "";
      });
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load employee tracking history");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchHistory(getDefaultFilters());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedRecord = useMemo(
    () => records.find((item) => item.attendanceId === selectedAttendanceId) || records[0] || null,
    [records, selectedAttendanceId]
  );

  const routePoints = selectedRecord?.routePoints || [];
  const mapPoints = useMemo(() => routePoints.map((point) => coordsOf(point)).filter(Boolean), [routePoints]);

  const metricCards = useMemo(
    () => [
      {
        label: "Sessions",
        value: summary?.totalSessions || 0,
        subtitle: "Date range me total tracking sessions",
        icon: <FiActivity />,
        tone: "blue",
      },
      {
        label: "Employees Covered",
        value: summary?.uniqueEmployees || 0,
        subtitle: "Tracking history me distinct employees",
        icon: <FiUsers />,
        tone: "violet",
      },
      {
        label: "Tracked Points",
        value: summary?.totalTrackedPoints || 0,
        subtitle: "Captured route points",
        icon: <LuRoute />,
        tone: "amber",
      },
      {
        label: "Distance Logged",
        value: fmtDistance(summary?.totalDistanceMeters),
        subtitle: "Range ka total travel",
        icon: <FiTrendingUp />,
        tone: "green",
      },
      {
        label: "Open Sessions",
        value: summary?.activeSessions || 0,
        subtitle: "Abhi bhi open / running",
        icon: <LuSignal />,
        tone: "rose",
      },
    ],
    [summary]
  );

  const timelineRows = useMemo(
    () =>
      routePoints.map((point, index) => ({
        ...point,
        index,
        coordsText: formatCoords(point),
        eventLabel: labelForEvent(point.type),
        tone: toneForEvent(point.type),
        segmentText: fmtDistance(point.segmentDistanceMeters),
        totalText: fmtDistance(point.totalDistanceMeters),
        stopped: isStopPoint(point, index, routePoints),
      })),
    [routePoints]
  );

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyPreset = (days) => {
    const today = new Date();
    const from = new Date(today);
    from.setDate(today.getDate() - (days - 1));
    const next = {
      ...filters,
      from: toDateInputValue(from),
      to: toDateInputValue(today),
    };
    setFilters(next);
    fetchHistory(next);
  };

  const resetFilters = () => {
    const next = getDefaultFilters();
    setFilters(next);
    fetchHistory(next);
  };

  const selectedState = getSessionState(selectedRecord);

  return (
    <div className="athp-page">
      <div className="athp-hero">
        <div className="athp-hero-copy">
          <span className="athp-eyebrow">Admin Employee Tab</span>
          <div className="athp-title-row">
            <h1>Employee Tracking History</h1>
            <span className="athp-range-pill">
              <FiCalendar />
              <span>
                {filters.from || "Start"} to {filters.to || "Today"}
              </span>
            </span>
          </div>
          <p>
            Har field employee ki route history, punch trail, meeting events aur movement timeline
            ko date range ke hisaab se nikaaliye.
          </p>
        </div>

        <div className="athp-hero-actions">
          <Button variant="light" className="athp-ghost-btn" onClick={() => applyPreset(1)}>
            Today
          </Button>
          <Button variant="light" className="athp-ghost-btn" onClick={() => applyPreset(7)}>
            Last 7 Days
          </Button>
          <Button variant="light" className="athp-ghost-btn" onClick={() => applyPreset(30)}>
            Last 30 Days
          </Button>
          <Button className="athp-refresh-btn" onClick={() => fetchHistory(filters, true)} disabled={refreshing}>
            <FiRefreshCcw />
            <span>{refreshing ? "Refreshing..." : "Refresh"}</span>
          </Button>
        </div>
      </div>

      <Card className="athp-filter-card">
        <Card.Body>
          <div className="athp-filter-head">
            <div className="athp-section-title">
              <FiFilter /> Filters
            </div>
            <div className="athp-section-subtitle">Employee aur date range change karke exact history nikaaliye.</div>
          </div>

          <Row className="g-3 align-items-end">
            <Col lg={4}>
              <Form.Label>Employee</Form.Label>
              <div className="athp-input-shell">
                <span className="athp-input-icon">
                  <FiUser />
                </span>
                <Form.Select
                  value={filters.employeeId}
                  onChange={(e) => handleFilterChange("employeeId", e.target.value)}
                >
                  <option value="">All field employees</option>
                  {employees.map((employee) => (
                    <option key={employee.employeeId} value={employee.employeeId}>
                      {employee.fullName} {employee.departmentName ? `• ${employee.departmentName}` : ""}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </Col>
            <Col md={6} lg={3}>
              <Form.Label>From</Form.Label>
              <Form.Control
                type="date"
                value={filters.from}
                onChange={(e) => handleFilterChange("from", e.target.value)}
              />
            </Col>
            <Col md={6} lg={3}>
              <Form.Label>To</Form.Label>
              <Form.Control type="date" value={filters.to} onChange={(e) => handleFilterChange("to", e.target.value)} />
            </Col>
            <Col lg={2}>
              <div className="athp-filter-actions">
                <Button className="athp-apply-btn" onClick={() => fetchHistory(filters)}>
                  <FiSearch />
                  <span>Apply</span>
                </Button>
                <Button variant="outline-secondary" onClick={resetFilters}>
                  Reset
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <div className="athp-metric-grid">
            {metricCards.map((metric) => (
              <Card key={metric.label} className={`athp-metric-card tone-${metric.tone}`}>
                <Card.Body>
                  <div className="athp-metric-icon">{metric.icon}</div>
                  <div className="athp-metric-copy">
                    <div className="athp-metric-label">{metric.label}</div>
                    <div className="athp-metric-value">{metric.value}</div>
                    <div className="athp-metric-subtitle">{metric.subtitle}</div>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>

          <Row className="g-4 align-items-start">
            <Col xl={4}>
              <Card className="athp-list-card">
                <Card.Body>
                  <div className="athp-section-head">
                    <div>
                      <div className="athp-section-title">
                        <FiActivity /> Tracking Sessions
                      </div>
                      <div className="athp-section-subtitle">
                        Kisi bhi session ko select karke uska full route aur timeline dekhiye.
                      </div>
                    </div>
                    <Badge bg="dark">{records.length}</Badge>
                  </div>

                  <div className="athp-session-list">
                    {records.length === 0 ? (
                      <div className="athp-empty-state">Is range me koi tracking session nahi mila.</div>
                    ) : (
                      records.map((record) => {
                        const state = getSessionState(record);
                        return (
                          <button
                            key={record.attendanceId}
                            type="button"
                            className={`athp-session-card ${
                              record.attendanceId === selectedRecord?.attendanceId ? "is-active" : ""
                            }`}
                            onClick={() => setSelectedAttendanceId(record.attendanceId)}
                          >
                            <div className="athp-session-top">
                              <div className="athp-session-avatar">{getInitials(record.fullName)}</div>
                              <div className="athp-session-copy">
                                <div className="athp-session-name-row">
                                  <strong>{record.fullName}</strong>
                                  <Badge bg={state.tone}>{state.label}</Badge>
                                </div>
                                <div className="athp-session-meta">{fmtDate(record.date)}</div>
                                <div className="athp-session-meta">
                                  {record.departmentName || "Department unavailable"} {record.roleName ? `• ${record.roleName}` : ""}
                                </div>
                              </div>
                            </div>
                            <div className="athp-session-stats">
                              <span>{record.routeSummary?.trackedPoints || 0} pts</span>
                              <span>{fmtDistance(record.routeSummary?.totalDistanceMeters)}</span>
                              <span>{fmtDuration(record.durationMinutes)}</span>
                            </div>
                            <div className="athp-session-address">{record.latestAddress || "Address unavailable"}</div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={8}>
              {selectedRecord ? (
                <>
                  <Card className="athp-detail-card">
                    <Card.Body>
                      <div className="athp-section-head">
                        <div>
                          <div className="athp-section-title">
                            <FiMapPin /> {selectedRecord.fullName} Route Detail
                          </div>
                          <div className="athp-section-subtitle">
                            {fmtDate(selectedRecord.date)} ka full movement path aur checkpoints.
                          </div>
                        </div>
                        <Badge bg={selectedState.tone}>{selectedState.label}</Badge>
                      </div>

                      <div className="athp-profile-strip">
                        <div className="athp-profile-avatar">{getInitials(selectedRecord.fullName)}</div>
                        <div className="athp-profile-main">
                          <div className="athp-profile-name-row">
                            <h3>{selectedRecord.fullName}</h3>
                            <span>{selectedRecord.departmentName || "Department"}</span>
                          </div>
                          <div className="athp-profile-badges">
                            <span className="athp-mini-pill">{selectedRecord.roleName || "Employee"}</span>
                            <span className="athp-mini-pill">{fmtDistance(selectedRecord.routeSummary?.totalDistanceMeters)}</span>
                            <span className="athp-mini-pill">{fmtDuration(selectedRecord.durationMinutes)}</span>
                            <span className="athp-mini-pill">{selectedRecord.routeSummary?.trackedPoints || 0} points</span>
                          </div>
                        </div>
                      </div>

                      <div className="athp-info-grid">
                        <InfoRow icon={<FiClock />} label="Punch In" value={fmtDateTime(selectedRecord.punchInAt)} />
                        <InfoRow icon={<FiClock />} label="Punch Out" value={fmtDateTime(selectedRecord.punchOutAt)} />
                        <InfoRow icon={<FiNavigation />} label="Last Updated" value={fmtDateTime(selectedRecord.lastUpdatedAt)} />
                        <InfoRow icon={<FiActivity />} label="Last Seen" value={fmtMinutesAgo(selectedRecord.lastSeenMinutesAgo)} />
                        <InfoRow icon={<LuSignal />} label="Meeting Events" value={selectedRecord.meetingEvents || 0} />
                        <InfoRow
                          icon={<FiMapPin />}
                          label="Office Distance"
                          value={fmtDistance(selectedRecord.officeDistanceMeters)}
                        />
                        <InfoRow icon={<LuRoute />} label="Total Travel" value={fmtDistance(selectedRecord.routeSummary?.totalDistanceMeters)} />
                        <InfoRow icon={<FiNavigation />} label="GPS" value={formatCoords(selectedRecord)} />
                      </div>

                      <div className="athp-address-block">
                        <div className="athp-address-title">Latest Address</div>
                        <div>{selectedRecord.latestAddress || "Address unavailable"}</div>
                      </div>

                      {mapPoints.length === 0 ? (
                        <div className="athp-empty-state mt-4">Is session ke liye koi GPS route point available nahi hai.</div>
                      ) : (
                        <div className="athp-map-wrap">
                          <MapApiStatusBadge />
                          <MapContainer center={mapPoints[0]} zoom={15} style={{ height: "100%", width: "100%" }}>
                            <TileLayer
                              attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors © <a href="https://carto.com/">CARTO</a>'
                              url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                            />
                            <FitMapBounds points={mapPoints} />
                            <Polyline positions={mapPoints} pathOptions={{ color: "#7dd3fc", weight: 9, opacity: 0.45 }} />
                            <Polyline positions={mapPoints} pathOptions={{ color: "#0f766e", weight: 4, opacity: 0.95 }} />
                            {routePoints.map((point, index) => {
                              const coords = coordsOf(point);
                              if (!coords) return null;
                              return (
                                <Marker
                                  key={`${point.type}-${point.at}-${index}`}
                                  position={coords}
                                  icon={getMarkerIcon(index, point, routePoints)}
                                >
                                  <Popup>
                                    <strong>{labelForEvent(point.type)}</strong>
                                    <br />
                                    {fmtDateTime(point.at)}
                                    <br />
                                    {point.address || "Address unavailable"}
                                    <br />
                                    GPS: {formatCoords(point)}
                                    <br />
                                    Segment: {fmtDistance(point.segmentDistanceMeters)}
                                    <br />
                                    Total: {fmtDistance(point.totalDistanceMeters)}
                                  </Popup>
                                </Marker>
                              );
                            })}
                          </MapContainer>
                        </div>
                      )}
                    </Card.Body>
                  </Card>

                  <Card className="athp-timeline-card mt-4">
                    <Card.Body>
                      <div className="athp-section-head">
                        <div>
                          <div className="athp-section-title">
                            <LuRoute /> Movement Timeline
                          </div>
                          <div className="athp-section-subtitle">
                            Har point ka event, time, segment distance aur note ek hi jagah.
                          </div>
                        </div>
                      </div>

                      <Table responsive hover className="athp-timeline-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Event</th>
                            <th>Time</th>
                            <th>Address</th>
                            <th>Coordinates</th>
                            <th>Segment</th>
                            <th>Total</th>
                            <th>Note</th>
                          </tr>
                        </thead>
                        <tbody>
                          {timelineRows.length === 0 ? (
                            <tr>
                              <td colSpan="8" className="text-center text-muted">
                                No timeline available
                              </td>
                            </tr>
                          ) : (
                            timelineRows.map((point) => (
                              <tr key={`${point.type}-${point.at}-${point.index}`}>
                                <td>{point.index + 1}</td>
                                <td>
                                  <span className={`athp-event-pill is-${point.tone}`}>{point.eventLabel}</span>
                                  {point.stopped ? <span className="athp-stop-tag">Stop</span> : null}
                                </td>
                                <td>{fmtDateTime(point.at)}</td>
                                <td>{point.address || "—"}</td>
                                <td>{point.coordsText}</td>
                                <td>{point.segmentText}</td>
                                <td>{point.totalText}</td>
                                <td>{point.note || "—"}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </>
              ) : (
                <Card className="athp-detail-card">
                  <Card.Body>
                    <div className="athp-empty-state">Session select karne ke baad yahan full tracking detail dikhegi.</div>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default AdminEmployeeTrackingHistoryPage;
